<script setup lang="ts">
import { computed } from 'vue';
import { AlertDialogDescription, AlertDialogTitle } from 'reka-ui';
import { AlertDialog } from '@/components/ui/alert-dialog';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { IconAlertTriangle, IconCheck, IconInfoCircle, IconAlertCircle } from '@tabler/icons-vue';
import MsButton from '@/components/ui/button/MsButton.vue';
import { DialogVariants } from '@/constants/dialog';

const dialog = useConfirmDialog();

const variantConfig = {
    [DialogVariants.Primary]: { icon: IconCheck, iconColor: 'text-green-600', bg: 'bg-green-100' },
    [DialogVariants.Danger]: { icon: IconAlertTriangle, iconColor: 'text-red-600', bg: 'bg-red-100' },
    [DialogVariants.Info]: { icon: IconInfoCircle, iconColor: 'text-blue-600', bg: 'bg-blue-100' },
    [DialogVariants.Warning]: { icon: IconAlertCircle, iconColor: 'text-yellow-700', bg: 'bg-yellow-100' },
};

const currentVariant = computed(() => variantConfig[dialog.variant] ?? variantConfig[DialogVariants.Info]);
const actionColor = computed(() => {
    if (dialog.variant === DialogVariants.Danger) {
        return 'danger';
    }

    if (dialog.variant === DialogVariants.Warning) {
        return 'warning';
    }

    return 'primary';
});

const isOpen = computed({
    get() {
        return dialog.active;
    },
    set(value: boolean) {
        if (!value) {
            resolveDialog(false);
        }
    },
});

function resolveDialog(resValue: boolean) {
    dialog.confirm(resValue);
}

function onAfterLeave() {
    if (!dialog.active) {
        dialog.resetState();
    }
}
</script>

<template>
    <AlertDialog
        v-model:open="isOpen"
        size="sm"
        :accessible-heading="false"
        @after:leave="onAfterLeave"
    >
        <div class="flex gap-6">
            <div
                class="flex size-12 shrink-0 items-center justify-center rounded-full"
                :class="currentVariant.bg"
            >
                <component
                    :is="currentVariant.icon"
                    :class="currentVariant.iconColor"
                    class="size-6"
                    aria-hidden="true"
                />
            </div>
            <div class="grow">
                <AlertDialogTitle class="mb-3 mt-1 text-xl font-semibold leading-6 text-black-900">
                    {{ dialog.title }}
                </AlertDialogTitle>
                <AlertDialogDescription class="text-sm text-black-500">
                    {{ dialog.message }}
                </AlertDialogDescription>
            </div>
        </div>

        <template
            v-if="!dialog.hideNoButton"
            #cancel
        >
            <MsButton
                variant="outline"
                color="neutral"
                :label="dialog.cancelText"
                type="button"
                :icon="dialog.cancelIcon"
                :disabled="dialog.loading"
                @click.capture="resolveDialog(false)"
            />
        </template>
        <template #action>
            <MsButton
                type="button"
                :label="dialog.actionText"
                :color="actionColor"
                :icon="dialog.actionIcon"
                :loading="dialog.loading"
                :disabled="dialog.loading"
                @click.capture="resolveDialog(true)"
            />
        </template>
    </AlertDialog>
</template>
